import { catalogueCategories, catalogueMarkets } from "./catalogue";

export const catalogueUsageMetricVersion = "catalogue-usage-v1" as const;
export const catalogueUsageGroups: ReadonlyArray<
  Readonly<{ id: string; label: string; minimumScopes: number }>
> = [
  { id: "created:all", label: "Reminders created", minimumScopes: 10 },
  { id: "origin:custom", label: "Created without a preset", minimumScopes: 10 },
  { id: "origin:preset", label: "Created from a preset", minimumScopes: 10 },
  ...[...catalogueMarkets, "not-specified"].map((market) => ({
    id: `market:${market}`,
    label: market === "not-specified" ? "No catalogue market" : market,
    minimumScopes: 10,
  })),
  ...catalogueCategories.map((category) => ({
    id: `category:${category.id}`,
    label: category.label,
    minimumScopes: ["insurance", "health-administration", "financial-tax"].includes(category.id)
      ? 20
      : 10,
  })),
  { id: "timing:not-applicable", label: "No suggested timing baseline", minimumScopes: 10 },
  { id: "timing:unclassified", label: "Timing comparison unavailable", minimumScopes: 10 },
  {
    id: "timing:existing-draft:unclassified",
    label: "Existing draft timings retained",
    minimumScopes: 10,
  },
  {
    id: "timing:system-adjusted:unclassified",
    label: "System-adjusted starting plan",
    minimumScopes: 10,
  },
  ...(["published-preset", "personal-preference"] as const).flatMap((source) => [
    {
      id: `timing:${source}:unchanged:unchanged`,
      label: source === "published-preset" ? "Preset timings kept" : "Personal timings kept",
      minimumScopes: 10,
    },
    ...(["increased", "decreased", "unchanged"] as const).map((count) => ({
      id: `timing:${source}:changed:${count}`,
      label: `${source === "published-preset" ? "Preset" : "Personal"} timings changed; ${count === "increased" ? "more reminders" : count === "decreased" ? "fewer reminders" : "same number of reminders"}`,
      minimumScopes: 10,
    })),
  ]),
];

export type CatalogueUsageAccumulator = ReadonlyArray<
  Readonly<{ group: string; count: number; scopes: number }>
>;
export type CatalogueUsageCell = Readonly<{
  group: string;
  state: "reported" | "withheld" | "incomplete";
  value: number | null;
}>;

/** One fixed release across every breakdown: never separately answer a suppressed complement. */
export function protectCatalogueUsageReport(
  rows: CatalogueUsageAccumulator,
  complete: boolean,
): ReadonlyArray<CatalogueUsageCell> {
  const allowed = new Set(catalogueUsageGroups.map((group) => group.id));
  const counts = new Map<string, { count: number; scopes: number }>();
  for (const row of rows) {
    if (
      !allowed.has(row.group) ||
      counts.has(row.group) ||
      !Number.isSafeInteger(row.count) ||
      !Number.isSafeInteger(row.scopes) ||
      row.count < 0 ||
      row.scopes < 0 ||
      row.scopes > row.count ||
      (row.count > 0 && row.scopes === 0)
    )
      throw new Error("Catalogue aggregate reconciliation failed.");
    counts.set(row.group, { count: row.count, scopes: row.scopes });
  }
  const total = counts.get("created:all") ?? { count: 0, scopes: 0 };
  for (const prefix of ["origin:", "market:", "category:", "timing:"]) {
    const sum = [...counts]
      .filter(([key]) => key.startsWith(prefix))
      .reduce((sum, [, value]) => sum + value.count, 0);
    if (sum !== total.count) throw new Error("Catalogue aggregate reconciliation failed.");
  }
  if ([...counts.values()].some((value) => value.scopes > total.scopes))
    throw new Error("Catalogue aggregate reconciliation failed.");
  const totalEligible = total.count === 0 || total.scopes >= 10;
  const allBreakdownsEligible =
    totalEligible &&
    catalogueUsageGroups.every((group) => {
      const cell = counts.get(group.id);
      return cell === undefined || cell.count === 0 || cell.scopes >= group.minimumScopes;
    });
  return catalogueUsageGroups.map((group) => {
    if (!complete) return { group: group.id, state: "incomplete", value: null };
    if (!(group.id === "created:all" ? totalEligible : allBreakdownsEligible))
      return { group: group.id, state: "withheld", value: null };
    return { group: group.id, state: "reported", value: counts.get(group.id)?.count ?? 0 };
  });
}

export function catalogueReportPeriod(period: string) {
  if (!/^[2-9]\d{3}-(0[1-9]|1[0-2])$/u.test(period))
    throw new Error("Choose a valid reporting month.");
  const year = Number(period.slice(0, 4));
  const month = Number(period.slice(5)) - 1;
  return {
    startsAt: Date.UTC(year, month, 1),
    closesAt: Date.UTC(year, month + 1, 1),
    linkageExpiresAt: Date.UTC(year, month + 1, 1) + 90 * 86_400_000,
    expiresAt: Date.UTC(year, month + 14, 1),
  };
}
